/**
 * Generic REST API client with retry and cancellation support
 *
 * Provides a robust client for REST API calls with:
 * - Automatic retry with exponential backoff (via axios-retry)
 * - Request cancellation support
 * - Enhanced error handling
 * - JSON schema validation (Zod)
 * - Support for all HTTP methods (GET, POST, PUT, DELETE, PATCH)
 *
 * @example
 * const client = new RestClient({
 *   baseURL: 'https://api.example.com',
 *   timeout: 30000,
 * });
 *
 * const response = await client.call({
 *   method: 'POST',
 *   endpoint: '/users',
 *   data: { name: 'John' },
 *   responseSchema: UserSchema,
 * });
 */

import axios, { type AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios';
import axiosRetry, { isNetworkOrIdempotentRequestError } from 'axios-retry';
import { z } from 'zod';
import { PlayerApiError, NetworkError, ValidationError } from '@/types/errors';

/**
 * Retry configuration for axios-retry
 */
export interface RetryConfig {
  retries?: number;
  retryDelay?: (retryCount: number, error: AxiosError) => number;
}

/**
 * REST client configuration
 */
export interface RestClientConfig {
  baseURL: string;
  timeout?: number;
  retryConfig?: RetryConfig;
  headers?: Record<string, string>;
}

/**
 * REST call configuration
 */
export interface RestCallConfig<TRequest = unknown, TResponse = unknown> {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  endpoint: string;
  data?: TRequest;
  params?: Record<string, string | number | boolean>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  requestSchema?: z.ZodSchema<TRequest>;
  responseSchema?: z.ZodSchema<TResponse>;
}

/**
 * Generic REST API client with retry and cancellation support
 *
 * Uses axios-retry for transparent retry logic with exponential backoff.
 * Handles REST API communication with error handling and JSON validation.
 */
export class RestClient {
  private readonly config: Required<Omit<RestClientConfig, 'retryConfig' | 'headers'>> & {
    headers?: Record<string, string>;
  };
  private readonly axiosInstance: AxiosInstance;

  constructor(config: RestClientConfig) {
    this.config = {
      timeout: config.timeout ?? 30000,
      baseURL: config.baseURL,
      headers: config.headers,
    };

    // Create dedicated axios instance
    this.axiosInstance = axios.create({
      baseURL: this.config.baseURL,
      timeout: this.config.timeout,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...this.config.headers,
      },
    });

    // Configure axios-retry for automatic retries
    axiosRetry(this.axiosInstance, {
      retries: config.retryConfig?.retries ?? 3,
      retryDelay: config.retryConfig?.retryDelay ?? axiosRetry.exponentialDelay,
      retryCondition: (error) => {
        // Retry on network errors, timeouts, and 5xx server errors
        if (isNetworkOrIdempotentRequestError(error)) {
          return true;
        }
        // Also retry on 5xx server errors
        const status = error.response?.status ?? 0;
        return status >= 500 && status < 600;
      },
    });
  }

  /**
   * Execute REST API call with automatic retry logic
   *
   * Retry is handled transparently by axios-retry interceptor.
   * Optional Zod schema validation provides runtime type checking for both request and response.
   *
   * @param config - REST call configuration
   * @returns Validated response data
   * @throws PlayerApiError, NetworkError, or ValidationError
   *
   * @example Basic POST request
   * const response = await client.call({
   *   method: 'POST',
   *   endpoint: '/users',
   *   data: { name: 'John Doe', email: 'john@example.com' },
   * });
   *
   * @example GET request with query parameters
   * const response = await client.call({
   *   method: 'GET',
   *   endpoint: '/users',
   *   params: { page: 1, limit: 10 },
   * });
   *
   * @example With Zod validation
   * import { CreateUserRequestSchema, UserResponseSchema } from '@/types/schemas';
   *
   * const response = await client.call({
   *   method: 'POST',
   *   endpoint: '/users',
   *   data: { name: 'John', email: 'john@example.com' },
   *   requestSchema: CreateUserRequestSchema,
   *   responseSchema: UserResponseSchema,
   * });
   * // Both request and response are validated and typed correctly
   *
   * @example With cancellation
   * const controller = new AbortController();
   * const response = await client.call({
   *   method: 'GET',
   *   endpoint: '/users',
   *   signal: controller.signal,
   * });
   * // Call controller.abort() to cancel the request
   */
  async call<TRequest = unknown, TResponse = unknown>(
    config: RestCallConfig<TRequest, TResponse>
  ): Promise<TResponse> {
    try {
      // Validate request data with Zod schema (if provided)
      if (config.requestSchema && config.data !== undefined) {
        const validation = config.requestSchema.safeParse(config.data);
        if (!validation.success) {
          throw new ValidationError(
            'Invalid request data',
            validation.error.issues.map((issue) => ({
              field: issue.path.join('.'),
              message: issue.message,
            }))
          );
        }
      }

      // Build axios request config
      const axiosConfig: AxiosRequestConfig = {
        method: config.method,
        url: config.endpoint,
        headers: config.headers,
        signal: config.signal,
      };

      // Add data for POST/PUT/PATCH requests
      if (config.data !== undefined && ['POST', 'PUT', 'PATCH'].includes(config.method)) {
        axiosConfig.data = config.data;
      }

      // Add query parameters for GET/DELETE requests (or any other)
      if (config.params) {
        axiosConfig.params = config.params;
      }

      // Execute request
      const response = await this.axiosInstance.request<TResponse>(axiosConfig);

      // Validate response with Zod schema (if provided)
      if (config.responseSchema) {
        const validation = config.responseSchema.safeParse(response.data);
        if (!validation.success) {
          throw new ValidationError(
            'Invalid response structure from API',
            validation.error.issues.map((issue) => ({
              field: issue.path.join('.'),
              message: issue.message,
            }))
          );
        }
        return validation.data;
      }

      return response.data;
    } catch (error) {
      throw this.handleError(error, config.endpoint);
    }
  }

  /**
   * Convenience method for GET requests
   *
   * @example
   * const users = await client.get<User[]>({
   *   endpoint: '/users',
   *   params: { page: 1 },
   *   responseSchema: UsersSchema,
   * });
   */
  async get<TResponse = unknown>(
    config: Omit<RestCallConfig<never, TResponse>, 'method' | 'data'>
  ): Promise<TResponse> {
    return this.call({ ...config, method: 'GET' });
  }

  /**
   * Convenience method for POST requests
   *
   * @example
   * const newUser = await client.post<CreateUserRequest, User>({
   *   endpoint: '/users',
   *   data: { name: 'John', email: 'john@example.com' },
   *   responseSchema: UserSchema,
   * });
   */
  async post<TRequest = unknown, TResponse = unknown>(
    config: Omit<RestCallConfig<TRequest, TResponse>, 'method'>
  ): Promise<TResponse> {
    return this.call({ ...config, method: 'POST' });
  }

  /**
   * Convenience method for PUT requests
   *
   * @example
   * const updatedUser = await client.put<UpdateUserRequest, User>({
   *   endpoint: '/users/123',
   *   data: { name: 'John Doe' },
   *   responseSchema: UserSchema,
   * });
   */
  async put<TRequest = unknown, TResponse = unknown>(
    config: Omit<RestCallConfig<TRequest, TResponse>, 'method'>
  ): Promise<TResponse> {
    return this.call({ ...config, method: 'PUT' });
  }

  /**
   * Convenience method for DELETE requests
   *
   * @example
   * await client.delete({
   *   endpoint: '/users/123',
   * });
   */
  async delete<TResponse = unknown>(
    config: Omit<RestCallConfig<never, TResponse>, 'method' | 'data'>
  ): Promise<TResponse> {
    return this.call({ ...config, method: 'DELETE' });
  }

  /**
   * Convenience method for PATCH requests
   *
   * @example
   * const updatedUser = await client.patch<Partial<User>, User>({
   *   endpoint: '/users/123',
   *   data: { name: 'John Doe' },
   *   responseSchema: UserSchema,
   * });
   */
  async patch<TRequest = unknown, TResponse = unknown>(
    config: Omit<RestCallConfig<TRequest, TResponse>, 'method'>
  ): Promise<TResponse> {
    return this.call({ ...config, method: 'PATCH' });
  }

  /**
   * Handle and normalize errors
   *
   * @private
   */
  private handleError(error: unknown, endpoint?: string): PlayerApiError {
    // Already a PlayerApiError
    if (error instanceof PlayerApiError) {
      return error;
    }

    // Axios error
    if (axios.isAxiosError(error)) {
      return this.handleAxiosError(error, endpoint);
    }

    // Generic error
    if (error instanceof Error) {
      return new PlayerApiError(error.message, 'UNKNOWN_ERROR', endpoint);
    }

    return new PlayerApiError('Unknown error occurred', 'UNKNOWN', endpoint);
  }

  /**
   * Handle axios-specific errors
   *
   * @private
   */
  private handleAxiosError(error: AxiosError, endpoint?: string): PlayerApiError {
    // Request cancellation
    if (axios.isCancel(error)) {
      return new PlayerApiError('Request was cancelled', 'REQUEST_CANCELLED', endpoint);
    }

    // Timeout
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      return new NetworkError(`Request timeout after ${this.config.timeout}ms`, error, true);
    }

    // Network errors
    if (
      error.code === 'ENOTFOUND' ||
      error.code === 'ECONNREFUSED' ||
      error.code === 'ECONNRESET'
    ) {
      return new NetworkError(`Network error: ${error.message}`, error, false);
    }

    // HTTP errors
    if (error.response) {
      return new PlayerApiError(
        `HTTP ${error.response.status}: ${error.message}`,
        error.code || 'HTTP_ERROR',
        endpoint,
        JSON.stringify(error.response.data),
        error.response.status
      );
    }

    return new PlayerApiError(error.message, error.code || 'AXIOS_ERROR', endpoint);
  }
}
